import React from "react";

import { useTranslation } from "react-i18next";

import { cn } from "@/src/lib/utils";

import { getWeekDays } from "../../../../utils/fast";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import {
  dateTimeColumn,
  lazyActionsColumn,
} from "../../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { fastTemplatePath } from "../../../routes/routes";

import type { TFunction } from "i18next";
import type { JSX } from "react";

import type { Template } from "../../../../api/FastAPI.types";
import type { WeekDays } from "../../../../utils/fast";
import type {
  DataTableColumnDef,
  RowAction,
} from "../../../dataTable/dataTable.types";

interface TemplateColumnsOptions {
  t: TFunction;
  channelId: string;
  firstDayOfWeek: WeekDays;
  loadActions: (template: Template) => Promise<RowAction[]>;
}

interface IconDayRendererProps {
  day: WeekDays;
  isActive: boolean;
}

interface WeekRendererProps {
  firstDayOfWeek: WeekDays;
  template: Template;
}

// Taille fixe (`flex-none`) : une pastille ne s'étire pas en ovale dans une cellule large.
function IconDayRenderer({ day, isActive }: IconDayRendererProps): JSX.Element {
  const { t } = useTranslation("common");

  return (
    <div
      className={cn(
        "flex size-6.25 flex-none items-center justify-center rounded-full border uppercase",
        isActive
          ? "border-border bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground",
      )}
    >
      {t(`${day}Letter`)}
    </div>
  );
}

function WeekRenderer({
  firstDayOfWeek,
  template,
}: WeekRendererProps): JSX.Element {
  return (
    <div className="flex gap-1.25">
      {getWeekDays(firstDayOfWeek).map((day) => (
        <IconDayRenderer key={day} day={day} isActive={template[day]} />
      ))}
    </div>
  );
}

export function getTemplateHref(channelId: string, template: Template): string {
  return fastTemplatePath(channelId, template.uuid);
}

export function templateColumns({
  t,
  channelId,
  firstDayOfWeek,
  loadActions,
}: TemplateColumnsOptions): DataTableColumnDef<Template>[] {
  return [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      meta: { searchable: true, label: t("name") },
      cell: ({ row }) => (
        <DataTableLinkCell href={getTemplateHref(channelId, row.original)}>
          {row.original.name}
        </DataTableLinkCell>
      ),
    },
    {
      accessorKey: "slots_errors_count",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("errors")} />
      ),
      meta: {
        label: t("errors"),
        width: "8%",
        minWidth: 100,
        align: "center",
      },
      cell: ({ getValue }) => {
        const count = getValue<number>();

        return count !== 0 ? (
          <div className="bg-destructive text-primary-foreground inline-flex size-5 items-center justify-center rounded-full">
            {count}
          </div>
        ) : null;
      },
    },
    {
      id: "associatedDays",
      header: () => <span>{t("associatedDays")}</span>,
      enableSorting: false,
      meta: {
        label: t("associatedDays"),
        width: "32%",
        minWidth: 225,
      },
      cell: ({ row }) => (
        <WeekRenderer firstDayOfWeek={firstDayOfWeek} template={row.original} />
      ),
    },
    dateTimeColumn<Template>("updated_at", t("update_date")),
    lazyActionsColumn<Template>(loadActions, (template) => template.name),
  ];
}
